// src/components/pages/b2b/ESG/EsgPillar.js
import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";

const OUTER_CIRCLE_COLORS = {
  environment: "#005F91",
  social: "#438FC7",
  governance: "#35A5AA",
};

const SEHO_LOGO_BROWN = "#EA9331";

const ESG_PILLAR_DATA = {
  sectionTitle: {
    ZH: "ESG 三大主軸",
    EN: "ESG: Three Pillars",
    JP: "ESGの三つの柱",
  },

  pillars: [
    {
      key: "environment",

      title: {
        ZH: "環境",
        EN: "Environment",
        JP: "環境",
      },

      text: {
        ZH: "以食品衛生與環境安全為核心，持續優化產品、設備與作業流程，\n降低營運對環境造成的負擔。",
        EN: "With food hygiene and environmental safety as our core focus, we continuously optimize products, equipment, and operational processes to reduce the environmental impact of our operations.",
        JP: "食品衛生と環境安全を核心として、製品、設備、業務プロセスを継続的に最適化し、事業運営による環境負荷の低減に取り組んでいます。",
      },

      list: {
        ZH: [
          "推動節能設備與作業流程優化，降低能源與資源耗用。",
          "減少包材使用，提升包裝可回收性與使用效率。",
          "在食品衛生、病媒防治與清潔系統中，優先導入低污染、環境友善材料。",
          "清潔與病媒防治作業兼顧食品安全與生態風險管理，降低對環境的影響。",
          "推動耗材減量、回收再利用與循環管理，協助客戶建立更有效率的衛生管理模式。",
          "持續進行能源與碳排管理，逐步提升整體營運效率。",
        ],
        EN: [
          "Promote energy-saving equipment and optimize operational processes to reduce energy and resource consumption.",
          "Reduce packaging material usage while improving recyclability and packaging efficiency.",
          "Prioritize the adoption of low-pollution and environmentally friendly materials in food hygiene, pest control, and cleaning systems.",
          "Ensure cleaning and pest control operations balance food safety and ecological risk management to minimize environmental impact.",
          "Promote consumable reduction, recycling, reuse, and circular management to help customers establish more efficient hygiene management systems.",
          "Continuously improve energy and carbon emission management to gradually enhance overall operational efficiency.",
        ],
        JP: [
          "省エネルギー設備と業務プロセスの最適化を推進し、エネルギーおよび資源消費を削減する。",
          "包装材の使用量を削減し、リサイクル性と使用効率を向上させる。",
          "食品衛生、害虫防除、および清掃システムにおいて、低汚染で環境に優しい材料を優先的に導入する。",
          "清掃および害虫防除作業において、食品安全と生態リスク管理を両立し、環境への影響を低減する。",
          "消耗品削減、リサイクル、再利用、および循環管理を推進し、お客様のより効率的な衛生管理体制構築を支援する。",
          "エネルギーおよび炭素排出管理を継続的に推進し、全体的な運営効率を段階的に向上させる。",
        ],
      },

      image: "/img/ESG/environment.webp",

      color: OUTER_CIRCLE_COLORS.environment,
    },
    {
      key: "social",

      title: {
        ZH: "社會",
        EN: "Social",
        JP: "社会",
      },

      text: {
        ZH: "以「安全、衛生、標準化」為基礎，提供穩定可靠的產品、服務與現場管理支持。",
        EN: "Based on the principles of safety, hygiene, and standardization, we provide stable and reliable products, services, and on-site management support.",
        JP: "『安全・衛生・標準化』を基盤として、安定かつ信頼性の高い製品、サービス、および現場管理サポートを提供しています。",
      },

      list: {
        ZH: [
          "所有產品與服務皆依循食品衛生、安全與相關法規標準執行。",
          "提供明確的衛生操作規範與使用指引，降低現場管理風險。",
          "重視員工專業能力與教育訓練，建立標準化與高品質的服務流程。",
          "建立安全、尊重與穩定的工作環境，提升團隊專業與執行品質。",
          "持續推廣食品衛生、環境清潔與病媒防治觀念，協助產業提升衛生管理水準。",
          "與客戶、社區及產業夥伴合作，共同建立更安全的食品衛生環境。",
        ],
        EN: [
          "All products and services comply with food hygiene, safety, and relevant regulatory standards.",
          "Provide clear hygiene operating procedures and usage guidelines to reduce on-site management risks.",
          "Emphasize employee professional capability and training to establish standardized and high-quality service processes.",
          "Create a safe, respectful, and stable working environment to improve team professionalism and execution quality.",
          "Continuously promote awareness of food hygiene, environmental cleaning, and pest control to help industries improve hygiene management standards.",
          "Collaborate with customers, communities, and industry partners to create safer food hygiene environments together.",
        ],
        JP: [
          "すべての製品とサービスは、食品衛生、安全、および関連法規基準に基づいて実施されます。",
          "明確な衛生作業基準と使用ガイドラインを提供し、現場管理リスクを低減する。",
          "従業員の専門能力と教育訓練を重視し、標準化された高品質なサービスプロセスを構築する。",
          "安全で尊重され、安定した職場環境を整備し、チームの専門性と実行品質を向上させる。",
          "食品衛生、環境清掃、および害虫防除に関する意識向上を継続的に推進し、業界全体の衛生管理レベル向上を支援する。",
          "お客様、地域社会、および業界パートナーと協力し、より安全な食品衛生環境を共に構築する。",
        ],
      },

      image: "/img/ESG/social.webp",

      color: OUTER_CIRCLE_COLORS.social,
    },
    {
      key: "governance",

      title: {
        ZH: "治理",
        EN: "Governance",
        JP: "ガバナンス",
      },

      text: {
        ZH: "建立制度化與可持續運作的管理機制，確保產品、服務與營運流程維持穩定品質。",
        EN: "Establish systematic and sustainable management mechanisms to ensure stable quality in products, services, and operational processes.",
        JP: "制度化され持続可能な管理体制を構築し、製品、サービス、および運営プロセスの安定した品質を確保しています。",
      },

      list: {
        ZH: [
          "建立內部管理、品質控管與風險管理制度，強化營運穩定性。",
          "落實法規遵循與誠信經營原則，維持透明與負責任的合作關係。",
          "持續強化食品衛生、安全管理與作業標準化流程。",
          "定期檢視供應鏈、產品與服務品質，提升整體管理效率。",
          "推動永續採購與供應鏈管理，建立穩定且可追溯的合作體系。",
          "持續揭露與改善 ESG 相關管理成果，提升企業長期經營韌性。",
        ],
        EN: [
          "Establish internal management, quality control, and risk management systems to strengthen operational stability.",
          "Implement regulatory compliance and integrity-based management principles to maintain transparent and responsible partnerships.",
          "Continuously strengthen food hygiene, safety management, and standardized operational procedures.",
          "Regularly review supply chains, products, and service quality to improve overall management efficiency.",
          "Promote sustainable procurement and supply chain management to establish stable and traceable cooperation systems.",
          "Continuously disclose and improve ESG-related management performance to enhance long-term corporate resilience.",
        ],
        JP: [
          "内部管理、品質管理、およびリスク管理制度を構築し、運営の安定性を強化する。",
          "法令遵守と誠実経営の原則を徹底し、透明性と責任ある協力関係を維持する。",
          "食品衛生、安全管理、および標準化された業務プロセスを継続的に強化する。",
          "サプライチェーン、製品、およびサービス品質を定期的に見直し、全体的な管理効率を向上させる。",
          "持続可能な調達とサプライチェーン管理を推進し、安定かつ追跡可能な協力体制を構築する。",
          "ESG関連の管理成果を継続的に開示・改善し、企業の長期的なレジリエンスを向上させる。",
        ],
      },

      image: "/img/ESG/governance.webp",

      color: OUTER_CIRCLE_COLORS.governance,
    },
  ],
};

function getLocalized(value, lang, fallback = "") {
  if (!value) return fallback;

  if (typeof value === "string") {
    return value;
  }

  return value[lang] || value.ZH || value.EN || value.JP || fallback;
}

function getLocalizedList(value, lang) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  return value[lang] || value.ZH || value.EN || value.JP || [];
}

function getFontFamily(fonts, lang) {
  if (typeof fonts === "string") {
    return fonts;
  }

  return (
    fonts?.[lang] || fonts?.ZH || '"LINE Seed TW","Noto Sans TC",system-ui'
  );
}

function EsgOuterCircle({
  pillar,
  active,
  lang,
  fontFamily,
  motion,
  position,
  onActivate,
}) {
  const size = {
    xs: 132,
    sm: 150,
    md: 178,
    lg: 268,
  };

  const strength = active ? 14 : 8;
  const hasActiveImage = active && pillar.image;

  return (
    <Box
      component="button"
      type="button"
      onMouseEnter={onActivate}
      onFocus={onActivate}
      sx={{
        position: "absolute",

        left: position.left,
        top: position.top,

        width: size,
        height: size,

        p: 0,
        m: 0,

        border: 0,
        borderRadius: "50%",

        backgroundColor: "transparent",

        transform: {
          xs: "translateX(-50%)",
          sm: "none",
          md: `translate(${motion.x * strength}px, ${motion.y * strength}px)`,
        },

        transition:
          "transform 260ms cubic-bezier(.2,.8,.2,1), filter 220ms ease",

        cursor: "pointer",

        appearance: "none",

        filter: active
          ? "drop-shadow(0 18px 32px rgba(0,0,0,0.16))"
          : "drop-shadow(0 8px 18px rgba(0,0,0,0.06))",

        "&:focus-visible": {
          outline: `3px solid ${pillar.color}`,
          outlineOffset: 6,
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 8,

          borderRadius: "50%",
          overflow: "hidden",

          bgcolor: "#ffffff",

          zIndex: 1,
        }}
      >
        {pillar.image ? (
          <Box
            component="img"
            src={pillar.image}
            alt={getLocalized(pillar.title, lang)}
            sx={{
              display: "block",

              width: "100%",
              height: "100%",

              objectFit: "cover",

              opacity: hasActiveImage ? 1 : 0,

              transform: hasActiveImage ? "scale(1)" : "scale(1.08)",

              transition:
                "opacity 260ms ease, transform 420ms cubic-bezier(.2,.8,.2,1)",

              filter: "saturate(1.03)",
            }}
          />
        ) : null}
      </Box>

      <Box
        sx={{
          position: "absolute",
          inset: 0,

          borderRadius: "50%",

          border: "2px dashed",
          borderColor: "#ffffff",

          opacity: active ? 1 : 0.74,

          transition: "opacity 220ms ease, border-color 220ms ease",

          zIndex: 3,
        }}
      />

      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,

          borderRadius: "50%",

          animation: "esgPillarOrbit 6.5s linear infinite",

          zIndex: 4,

          "@keyframes esgPillarOrbit": {
            "0%": {
              transform: "rotate(0deg)",
            },
            "100%": {
              transform: "rotate(360deg)",
            },
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -4,
            left: "50%",

            width: {
              xs: 10,
              md: 12,
            },

            height: {
              xs: 10,
              md: 12,
            },

            borderRadius: "50%",

            bgcolor: "#ffffff",

            boxShadow: "0 0 0 8px rgba(255,255,255,0.22)",

            transform: "translateX(-50%)",
          }}
        />
      </Box>

      <Typography
        sx={{
          position: "absolute",

          left: "50%",
          top: "50%",

          transform: "translate(-50%, -50%)",

          width: {
            xs: "88%",
            sm: "72%",
          },

          fontFamily,
          fontWeight: 700,

          fontSize: {
            xs: 13,
            sm: 19,
            md: 22,
            lg: 35,
          },

          lineHeight: 1.15,

          color: pillar.color,

          textAlign: "center",

          whiteSpace: "pre-line",

          opacity: hasActiveImage ? 0 : 1,

          transition: "opacity 180ms ease",

          zIndex: 5,
        }}
      >
        {getLocalized(pillar.title, lang)}
      </Typography>
    </Box>
  );
}

export default function EsgPillar({ lang = "ZH", fonts }) {
  const fontFamily = getFontFamily(fonts, lang);

  const [activeKey, setActiveKey] = useState("environment");

  const [motion, setMotion] = useState({
    x: 0,
    y: 0,
  });

  const pillars = ESG_PILLAR_DATA.pillars;

  const activePillar = useMemo(
    () => pillars.find((pillar) => pillar.key === activeKey) || pillars[0],
    [activeKey, pillars],
  );

  const sectionTitle = getLocalized(ESG_PILLAR_DATA.sectionTitle, lang);

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();

    const nextX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;

    const nextY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;

    setMotion({
      x: Math.max(-1, Math.min(1, nextX)),
      y: Math.max(-1, Math.min(1, nextY)),
    });
  };

  const handleMouseLeave = () => {
    setMotion({
      x: 0,
      y: 0,
    });
  };

  const circlePositions = {
    environment: {
      left: {
        xs: "50%",
        sm: "42%",
        md: "42%",
        lg: "42%",
      },

      top: {
        xs: 80,
        sm: 0,
        md: "2%",
        lg: "2%",
      },
    },

    social: {
      left: {
        xs: "50%",
        sm: "2%",
        md: "8%",
        lg: "8%",
      },

      top: {
        xs: 260,
        sm: 178,
        md: "37%",
        lg: "37%",
      },
    },

    governance: {
      left: {
        xs: "50%",
        sm: "44%",
        md: "50%",
        lg: "50%",
      },

      top: {
        xs: 440,
        sm: 358,
        md: "55%",
        lg: "55%",
      },
    },
  };

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        

        bgcolor: "#ffffff",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily,
            fontWeight: 700,
            fontSize: {
              xs: 22,
              md: 40,
            },

            mb: {xs: 2, md: 2, lg: 4},
            letterSpacing: ".01em",
            color: activePillar.color,
          }}
        >
          {sectionTitle}
        </Typography>
      </Box>

      <Box
        sx={{
          width: "100vw",

          ml: "calc(50% - 50vw)",

          bgcolor: activePillar.color,

          transition: "background-color 260ms ease",

          py: {
            xs: 5,
            md: 6,
          },

          px: {
            xs: 2,
            md: 4,
          },
        }}
      >
        <Box
          sx={{
            maxWidth: 1600,

            mx: "auto",

            display: "grid",

            gridTemplateColumns: {
              xs: "minmax(128px, 0.42fr) minmax(0, 0.58fr)",
              sm: "minmax(280px, 0.46fr) minmax(0, 0.54fr)",
              md: "minmax(360px, 0.95fr) minmax(0, 1.05fr)",
              lg: "minmax(420px, 0.95fr) minmax(0, 1.05fr)",
            },

            gap: {
              xs: 2,
              sm: 3,
              md: 6,
              lg: 8,
            },

            alignItems: "center",
          }}
        >
        {/* LEFT: ESG circle system */}
        <Box
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: "relative",

            minHeight: {
              xs: 610,
              sm: 540,
              md: 600,
              lg: 560,
            },

            overflow: "visible",

            transform: {
              xs: "none",
              md: `translate(${motion.x * 10}px, ${motion.y * 10}px)`,
            },

            transition: "transform 280ms cubic-bezier(.2,.8,.2,1)",
          }}
        >
          {pillars.map((pillar) => (
            <EsgOuterCircle
              key={pillar.key}
              pillar={pillar}
              active={pillar.key === activeKey}
              lang={lang}
              fontFamily={fontFamily}
              motion={motion}
              position={circlePositions[pillar.key]}
              onActivate={() => setActiveKey(pillar.key)}
            />
          ))}
        </Box>
        {/* RIGHT: Active ESG pillar content */}
        <Box
          sx={{
            minWidth: 0,

            pl: {
              xs: 0.5,
              sm: 1,
              md: 2,
              lg: 2,
            },

            alignSelf: {
              xs: "center",
              sm: "center",
              md: "center",
              lg: "center",
            },
          }}
        >
          <Box
            sx={{
              mt: {
                xs: 2.5,
                md: 3.5,
              },

              borderLeft: {
                xs: "3px solid #ffffff",
                sm: "4px solid #ffffff",
                md: "5px solid #ffffff",
              },

              pl: {
                xs: 1.5,
                sm: 2,
                md: 3,
              },
            }}
          >
            <Typography
              sx={{
                fontFamily,
                fontWeight: 700,

                fontSize: {
                  xs: 20,
                  sm: 30,
                  md: 38,
                  lg: 45,
                },

                lineHeight: 1.25,

                color: "#ffffff",

                mb: 1.5,
              }}
            >
              {getLocalized(activePillar.title, lang)}
            </Typography>

            <Typography
              sx={{
                fontFamily,
                fontWeight: 400,

                fontSize: {
                  xs: 13,
                  sm: 17,
                  md: 21,
                  lg: 25,
                },

                whiteSpace: "pre-line",

                lineHeight: 1.85,

                color: "#ffffff",

                mb: 2,
              }}
            >
              {getLocalized(activePillar.text, lang)}
            </Typography>

            <Box
              component="ul"
              sx={{
                m: 0,

                pl: {
                  xs: 2.5,
                  md: 3,
                },
              }}
            >
              {getLocalizedList(activePillar.list, lang).map((item, index) => (
                <Typography
                  key={index}
                  component="li"
                  sx={{
                    fontFamily,

                    fontSize: {
                      xs: 12.5,
                      sm: 15,
                      md: 18,
                      lg: 20,
                    },

                    lineHeight: 1.9,

                    color: "#ffffff",

                    mb: 0.75,

                    "&::marker": {
                      color: "#ffffff",
                    },
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
        </Box>
      </Box>
    </Box>
  );
}
